import { getSession, signIn } from 'next-auth/react'
import { getPersona, PERSONA_HEADER } from '@/lib/persona'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/**
 * An API error that keeps the HTTP status, so a caller can tell a 404 (not
 * yours), 403 (role not set up) and 409 (stale version) apart. It is still an
 * `Error` with the server's `detail` as the message, so existing callers that
 * only read `message` are unaffected.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/** Status of an error thrown by the API client, or null for a network or other error. */
export function apiErrorStatus(err: unknown): number | null {
  return err instanceof ApiError ? err.status : null
}

function detailMessage(detail: unknown): string {
  if (typeof detail === 'string' && detail) return detail
  // FastAPI 422 bodies carry a list of {loc, msg}; show the messages, not "[object Object]".
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
      .filter(Boolean)
    if (parts.length) return parts.join('; ')
  }
  // Object details, e.g. the Auto bridge mirror check {detail, field} or {message}.
  if (detail && typeof detail === 'object') {
    const record = detail as { detail?: unknown; message?: unknown; field?: unknown }
    const text = typeof record.detail === 'string' ? record.detail : typeof record.message === 'string' ? record.message : ''
    if (text) return typeof record.field === 'string' && record.field ? `${text} (${record.field})` : text
  }
  return 'An API error occurred.'
}

let signingIn: Promise<unknown> | null = null

/** Starts the Keycloak sign-in once, however many polling requests see the 401 or refresh error. */
function signInOnce(): Promise<unknown> {
  if (!signingIn) signingIn = signIn('keycloak')
  return signingIn
}

/**
 * Sets `X-AIO-Persona` (plans/00 B5) from the window's persona at request time,
 * unless the caller set it explicitly for this one request.
 */
function applyPersona(headers: Headers): void {
  if (!headers.has(PERSONA_HEADER)) headers.set(PERSONA_HEADER, getPersona())
}

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }
  applyPersona(headers)

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signInOnce()
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    await signInOnce()
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    // Rule 08 bodies carry {error: {code, message}}; older ones carry {detail}.
    const envelope = errorData?.error && typeof errorData.error === 'object' ? errorData.error : null
    const message = typeof envelope?.message === 'string' && envelope.message ? envelope.message : detailMessage(errorData?.detail)
    throw new ApiError(message, response.status, typeof envelope?.code === 'string' ? envelope.code : undefined)
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a file upload (multipart/form-data POST).
   * Content-Type is left unset: the browser sets it with the multipart boundary.
   */
  upload: <T = unknown>(endpoint: string, formData: FormData, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

/**
 * Fetch a binary file with the Bearer token and the persona header and return
 * it as a Blob, e.g. to show a document, a drone video or a call recording
 * through a blob: URL. Pass `headers` for a `Range` request. The caller owns
 * the object URL it creates from the Blob.
 */
export async function fetchAuthenticatedBlob(
  endpoint: string,
  signal?: AbortSignal,
  init?: { headers?: HeadersInit }
): Promise<Blob> {
  const session = await getSession()
  const headers = new Headers(init?.headers || {})
  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }
  applyPersona(headers)

  const response = await fetch(`${API_URL}${BASE_PATH}${endpoint}`, { headers, signal })

  if (response.status === 401) {
    await signInOnce()
    throw new Error('Your session has expired. Please sign in again.')
  }
  if (!response.ok) {
    throw new ApiError(`Download failed (${response.status})`, response.status)
  }

  return response.blob()
}

/** Trigger a browser save-as for a Blob already in memory. */
export function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(objectUrl)
}

// Default export for backward compatibility
export default apiClientFetch
